import GoogleMapIconTypesEnum from '@models/enums/GoogleMapIconTypesEnum';
import { IGoogleMapRef } from '@models/IGoogleMap';
import { IStoreLocatorDealer } from '@models/IStoreLocatorDealer';
import GoogleMap from '@molecules/GoogleMap/GoogleMap';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import DealersMapPinTooltip from './DealersMapPinTooltip';

export interface IDealersMapRef {
  readonly selectDealerOnMap: (dealer: IStoreLocatorDealer) => void;
  readonly closeActiveTooltip: () => void;
}

interface Props {
  readonly dealers: IStoreLocatorDealer[];
  readonly googleMapId: string;
  readonly googleMapsApiKey: string;
  readonly goToDealerLabel: string;
  readonly onSetSelectedDealer?: (dealer: IStoreLocatorDealer | null) => void;
}

const DealersMap = forwardRef<IDealersMapRef, Props>(
  (
    {
      dealers,
      googleMapId,
      googleMapsApiKey,
      goToDealerLabel,
      onSetSelectedDealer,
    },
    ref
  ) => {
    const googleMapRef = useRef<IGoogleMapRef>(null);
    const mapLocations = useMemo(
      () =>
        dealers.map((dealer) => ({
          name: dealer.title,
          latitude: dealer.latitude,
          longitude: dealer.longitude,
        })),
      [dealers]
    );

    const handleCloseActiveTooltip = () => {
      googleMapRef.current?.closeMarkerInfoWindow();
      onSetSelectedDealer?.(null);
    };

    const handleMapMarkerTooltipOpen = (dealer: IStoreLocatorDealer) => {
      const tooltipContainer = document.createElement('div');
      const root = createRoot(tooltipContainer);
      root.render(
        <DealersMapPinTooltip
          dealer={dealer}
          goToDealerLabel={goToDealerLabel}
          onCloseTooltip={handleCloseActiveTooltip}
        />
      );

      googleMapRef.current?.openMarkerInfoWindow(dealer, tooltipContainer);
    };

    const handleMarkerSelect = (
      marker: google.maps.marker.AdvancedMarkerElement
    ) => {
      const selectedDealer = dealers.find(
        (dealer) =>
          parseFloat(dealer.latitude) === marker.position?.lat &&
          parseFloat(dealer.longitude) === marker.position?.lng &&
          dealer.title === marker.title
      );
      if (!selectedDealer) return;

      onSetSelectedDealer?.(selectedDealer);
      handleMapMarkerTooltipOpen(selectedDealer);
    };

    useImperativeHandle(ref, () => ({
      selectDealerOnMap: (dealer) => {
        googleMapRef.current?.selectMarkerOnMap(dealer);
        handleMapMarkerTooltipOpen(dealer);
      },
      closeActiveTooltip: handleCloseActiveTooltip,
    }));

    return (
      <div
        className="flex min-h-[400px] w-full flex-col max-lg:h-[calc(100vh-300px)] lg:sticky lg:top-0 lg:block lg:w-1/2"
        data-testid="CT-30-Store-Locator-Dealers-Map"
      >
        <div className="flex-1 overflow-hidden max-lg:rounded-b-[10px] lg:h-full lg:flex-none lg:rounded-[10px]">
          <GoogleMap
            ref={googleMapRef}
            mapId={googleMapId}
            googleMapsApiKey={googleMapsApiKey}
            defaultIconType={GoogleMapIconTypesEnum.Default}
            selectedIconType={GoogleMapIconTypesEnum.DefaultSelected}
            locations={mapLocations}
            zoomButtonsPositionClassName="bottom-[15px] right-[15px]"
            onMarkerClick={handleMarkerSelect}
          />
        </div>
      </div>
    );
  }
);

export default DealersMap;
